import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, Edit3, Plus, Save, X, Trash2, MapPin, Paperclip, RefreshCcw, GraduationCap, Sparkles,
} from 'lucide-react';
import {
  getExperiences,
  getInteractionsByExperience,
  createInteraction,
  updateInteraction,
  deleteInteraction,
} from '../services/legacyApiService';
import { LegacyExperience, LegacyInteraction } from '../types/legacy';
import { uploadFile } from '../services/uploadService';
import MapCoordinatePicker from '../components/MapCoordinatePicker';
import { useAuth } from '../contexts/AuthContext';
import { computeExperienceFlowStatus } from '../utils/experienceFlow';
import { generateIntentQuickReplies } from '../services/quickRepliesService';

const emptyInteraction: Omit<LegacyInteraction, 'id'> = {
  idExperience: undefined,
  displayName: '',
  description: '',
  action: '',
  recoveryPrompt: '',
  promptRecupero: '',
  promptDiRecupero: '',
  geminiPrompt: '',
  mlDisabled: false,
  resetContexts: false,
  isFallback: false,
  genericInteraction: false,
  latitude: null,
  longitude: null,
  trainingPhraseDTO: [],
  inputContextNamesDTO: [],
  outputContextsDTO: [],
  messagesDTO: [{ textStringDTOList: [{ text: '' }] }],
  eventsDTO: [],
};

type EditTab = 'base' | 'training' | 'risposte' | 'mappa' | 'media';
type ExperienceScope = 'all' | number;

const normalizeQuickText = (value: string): string =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const isTokenLikeQuickReply = (value: string): boolean => {
  const v = normalizeQuickText(value);
  if (!v) return false;
  if (v.startsWith('input.')) return true;
  if (v.includes('_')) return true;
  return /^[a-z0-9.-]+$/i.test(v) && !v.includes(' ');
};

const normalizeTrainingTypeValue = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object') {
    const obj = value as { name?: string; id?: number | string };
    if (typeof obj.name === 'string' && obj.name.trim()) return obj.name.trim();
    if (obj.id !== undefined && obj.id !== null) return String(obj.id).trim();
  }
  return '';
};

const ensurePrimaryMessageText = (
  messages: LegacyInteraction['messagesDTO'],
  fallbackText: string,
): LegacyInteraction['messagesDTO'] => {
  const safeMessages = Array.isArray(messages) ? [...messages] : [];
  if (!safeMessages[0]) {
    safeMessages[0] = { textStringDTOList: [{ text: fallbackText }] };
    return safeMessages;
  }
  const first = safeMessages[0];
  const textRows = Array.isArray(first.textStringDTOList) ? [...first.textStringDTOList] : [];
  const firstText = String(textRows[0]?.text || '').trim();
  if (!firstText) {
    if (textRows.length === 0) textRows.push({ text: fallbackText });
    else textRows[0] = { ...textRows[0], text: fallbackText };
  }
  safeMessages[0] = {
    ...first,
    textStringDTOList: textRows,
  };
  return safeMessages;
};

const normalizeIntentText = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase();

const START_INTENT_ACTION = 'avviata';
const DIRECT_INTENTS_ENABLED = String(import.meta.env.VITE_ENABLE_DIRECT_INTENTS || 'true').trim().toLowerCase() !== 'false';
const START_EVENT_NAMES = ['start', 'input.welcome', 'welcome'] as const;

const isStartLikeInteraction = (interaction: LegacyInteraction): boolean => {
  const displayName = normalizeIntentText(interaction.displayName);
  const action = normalizeIntentText(interaction.action);
  const events = (interaction.eventsDTO || []).map((event) => normalizeIntentText(event?.name));
  return (
    interaction.isFallback === true
    || events.includes('input.welcome')
    || events.includes('welcome')
    || events.includes('start')
    || displayName.includes('welcome')
    || displayName.includes('start')
    || displayName.includes('iniz')
    || action.includes('welcome')
    || action.includes('start')
    || action.includes('iniz')
  );
};

const isStartLikeDraft = (interaction: Omit<LegacyInteraction, 'id'>): boolean =>
  isStartLikeInteraction({
    ...(interaction as LegacyInteraction),
    id: 0,
    displayName: interaction.displayName || '',
    trainingPhraseDTO: Array.isArray(interaction.trainingPhraseDTO) ? interaction.trainingPhraseDTO : [],
    messagesDTO: Array.isArray(interaction.messagesDTO) ? interaction.messagesDTO : [],
    eventsDTO: Array.isArray(interaction.eventsDTO) ? interaction.eventsDTO : [],
  });

const buildCanonicalStartEvents = (
  events: LegacyInteraction['eventsDTO'] | undefined,
): LegacyInteraction['eventsDTO'] => {
  const seen = new Set<string>();
  const normalizedRows: NonNullable<LegacyInteraction['eventsDTO']> = [];

  const push = (rawName: unknown) => {
    const name = normalizeIntentText(rawName);
    if (!name || seen.has(name)) return;
    seen.add(name);
    normalizedRows.push({ name });
  };

  START_EVENT_NAMES.forEach((eventName) => push(eventName));
  (Array.isArray(events) ? events : []).forEach((row) => push(row?.name));
  return normalizedRows;
};

const resolveInteractionExperienceId = (
  interaction: Partial<LegacyInteraction> | null | undefined,
  fallbackExperienceId?: number,
): number | null => {
  const raw = Number(interaction?.idExperience || fallbackExperienceId || 0);
  if (!Number.isFinite(raw) || raw <= 0) return null;
  return Math.trunc(raw);
};

const scoreStartIntent = (interaction: LegacyInteraction): number => {
  const action = normalizeIntentText(interaction.action);
  const displayName = normalizeIntentText(interaction.displayName);
  const eventNames = (interaction.eventsDTO || []).map((row) => normalizeIntentText(row?.name));
  let score = 0;
  if (action === START_INTENT_ACTION) score += 200;
  if (eventNames.includes('start')) score += 120;
  if (eventNames.includes('input.welcome')) score += 100;
  if (eventNames.includes('welcome')) score += 80;
  if (displayName.includes('welcome') || displayName.includes('start') || displayName.includes('iniz')) score += 60;
  return score;
};

const pickPrimaryStartIntent = (rows: LegacyInteraction[]): LegacyInteraction | null => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return [...rows].sort((a, b) => {
    const scoreDiff = scoreStartIntent(b) - scoreStartIntent(a);
    if (scoreDiff !== 0) return scoreDiff;
    return Number(a.id || 0) - Number(b.id || 0);
  })[0] || null;
};

const InteractionsLegacy = () => {
  const { isSuperAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedExperienceId = Number(searchParams.get('experienceId') || '');
  const requestedExperienceIdSafe = Number.isFinite(requestedExperienceId) && requestedExperienceId > 0
    ? requestedExperienceId
    : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [experiences, setExperiences] = useState<LegacyExperience[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<ExperienceScope>('all');
  const [searchText, setSearchText] = useState('');
  const [interactions, setInteractions] = useState<LegacyInteraction[]>([]);

  const [editing, setEditing] = useState<LegacyInteraction | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [editForm, setEditForm] = useState<Omit<LegacyInteraction, 'id'>>(emptyInteraction);
  const [editTab, setEditTab] = useState<EditTab>('base');
  const [saving, setSaving] = useState(false);
  const [deleteConfirmInteraction, setDeleteConfirmInteraction] = useState<LegacyInteraction | null>(null);

  const [trainingInput, setTrainingInput] = useState('');
  const [responseInput, setResponseInput] = useState('');
  const [quickReplyInput, setQuickReplyInput] = useState('');

  const [mediaUrl, setMediaUrl] = useState<string>('');
  const [mediaMime, setMediaMime] = useState<string>(''); // es. 'image/jpeg', 'audio/mpeg', 'video/mp4'
  const [mediaUploading, setMediaUploading] = useState(false);
  const [quickRepliesAiLoading, setQuickRepliesAiLoading] = useState(false);

  const loadExperiences = async () => {
    const exp = await getExperiences();
    setExperiences(exp);
    const first = exp[0]?.id ?? null;
    return { first, exp };
  };

  const loadInteractions = async (
    experienceScope: ExperienceScope,
    query?: string,
    sourceExperiences?: LegacyExperience[],
  ) => {
    if (experienceScope === 'all') {
      // Vista superadmin: aggrega intent da tutte le esperienze disponibili.
      const expSource = sourceExperiences || experiences;
      const chunks = await Promise.all(
        expSource.map(async (experience) => {
          const rows = await getInteractionsByExperience({ idExperience: experience.id, searchText: query });
          return rows.map((row) => ({
            ...row,
            idExperience: row.idExperience || experience.id,
          }));
        }),
      );
      setInteractions(chunks.flat());
      return;
    }

    const data = await getInteractionsByExperience({ idExperience: experienceScope, searchText: query });
    setInteractions(data);
  };

  const bootstrap = async () => {
    try {
      setLoading(true);
      setError(null);
      const { first, exp } = await loadExperiences();
      const initialScope: ExperienceScope =
        requestedExperienceIdSafe && exp.some((item) => item.id === requestedExperienceIdSafe)
          ? requestedExperienceIdSafe
          : (isSuperAdmin ? 'all' : (first ?? 'all'));
      setSelectedExperience(initialScope);
      if (initialScope === 'all') {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ experienceId: String(initialScope) }, { replace: true });
      }
      await loadInteractions(initialScope, undefined, exp);
    } catch (err) {
      console.error(err);
      setError('Errore nel caricamento interazioni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void bootstrap(); }, []);

  useEffect(() => {
    if (loading || !requestedExperienceIdSafe) return;
    if (selectedExperience === requestedExperienceIdSafe) return;
    if (!experiences.some((item) => item.id === requestedExperienceIdSafe)) return;
    setSelectedExperience(requestedExperienceIdSafe);
    void loadInteractions(requestedExperienceIdSafe, searchText || undefined);
  }, [
    loading,
    requestedExperienceIdSafe,
    selectedExperience,
    experiences,
    searchText,
  ]);

  const filtered = useMemo(() => {
    if (!searchText.trim()) return interactions;
    const q = searchText.toLowerCase();
    return interactions.filter((interaction) => {
      const training = interaction.trainingPhraseDTO?.map((t) => t.partsListDTO?.[0]?.text).join(' ') || '';
      return [interaction.displayName, interaction.description, training]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [interactions, searchText]);

  const fallbackTrainingType = useMemo(() => {
    const known = interactions
      .flatMap((item) => (Array.isArray(item.trainingPhraseDTO) ? item.trainingPhraseDTO : []))
      .map((row) => normalizeTrainingTypeValue(row?.type))
      .find(Boolean);
    return known || 'text';
  }, [interactions]);

  const selectedExperienceData = useMemo(
    () => (typeof selectedExperience === 'number'
      ? experiences.find((item) => item.id === selectedExperience) || null
      : null),
    [selectedExperience, experiences],
  );

  const selectedFlowStatus = useMemo(() => {
    if (!selectedExperienceData) return null;
    return computeExperienceFlowStatus(selectedExperienceData, interactions, {
      includeIntentChecks: true,
      includePromptCheck: false,
    });
  }, [selectedExperienceData, interactions]);

  const fallbackIntentCount = useMemo(
    () => interactions.filter((item) => Boolean(item?.isFallback)).length,
    [interactions],
  );

  const openNew = () => {
    if (selectedExperience === 'all') {
      setError('Per creare un intent seleziona prima una singola esperienza.');
      return;
    }
    const targetExperienceId = selectedExperience;
    setIsNew(true);
    setEditing(null);
    setEditForm({ ...emptyInteraction, idExperience: targetExperienceId });
    setEditTab('base');
    setTrainingInput('');
    setResponseInput('');
    setQuickReplyInput('');
    setMediaUrl('');
    setMediaMime('');
  };

  const openEditor = (interaction: LegacyInteraction) => {
    setIsNew(false);
    setEditing(interaction);
    setEditForm({
      idExperience: interaction.idExperience,
      displayName: interaction.displayName || '',
      description: interaction.description || '',
      action: interaction.action || '',
      recoveryPrompt: interaction.recoveryPrompt
        || interaction.promptRecupero
        || interaction.promptDiRecupero
        || interaction.geminiPrompt
        || '',
      promptRecupero: interaction.promptRecupero || '',
      promptDiRecupero: interaction.promptDiRecupero || '',
      geminiPrompt: interaction.geminiPrompt || '',
      mlDisabled: interaction.mlDisabled ?? false,
      resetContexts: interaction.resetContexts ?? false,
      isFallback: interaction.isFallback ?? false,
      genericInteraction: interaction.genericInteraction ?? false,
      latitude: interaction.latitude ?? null,
      longitude: interaction.longitude ?? null,
      trainingPhraseDTO: interaction.trainingPhraseDTO ? [...interaction.trainingPhraseDTO] : [],
      inputContextNamesDTO: interaction.inputContextNamesDTO ? [...interaction.inputContextNamesDTO] : [],
      outputContextsDTO: interaction.outputContextsDTO ? [...interaction.outputContextsDTO] : [],
      messagesDTO: interaction.messagesDTO ? [...interaction.messagesDTO] : [{ textStringDTOList: [{ text: '' }] }],
      eventsDTO: interaction.eventsDTO ? [...interaction.eventsDTO] : [],
    });
    setEditTab('base');
    setTrainingInput('');
    setResponseInput('');
    setQuickReplyInput('');
    const existingMedia = interaction.messagesDTO?.[0]?.mediaDTO?.links?.[0] ?? '';
    setMediaUrl(existingMedia);
    // Ricostruisce il mime type dal tipo legacy (2=video,3=audio,4=image) per il preview
    const legacyType = Number(interaction.messagesDTO?.[0]?.mediaDTO?.type || 0);
    const mimeFromLegacy = legacyType === 4 ? 'image/' : legacyType === 3 ? 'audio/' : legacyType === 2 ? 'video/' : '';
    setMediaMime(mimeFromLegacy);
  };

  const closeEditor = () => { setEditing(null); setIsNew(false); };

  const addTraining = () => {
    if (!trainingInput.trim()) return;
    setEditForm((prev) => ({
      ...prev,
      trainingPhraseDTO: [
        ...prev.trainingPhraseDTO,
        { type: fallbackTrainingType, partsListDTO: [{ text: trainingInput.trim() }] },
      ],
    }));
    setTrainingInput('');
  };

  const removeTraining = (idx: number) => {
    setEditForm((prev) => ({
      ...prev,
      trainingPhraseDTO: prev.trainingPhraseDTO.filter((_, i) => i !== idx),
    }));
  };

  const addResponse = () => {
    if (!responseInput.trim()) return;
    setEditForm((prev) => {
      const msgs = [...prev.messagesDTO];
      if (!msgs[0]) {
        msgs[0] = { textStringDTOList: [] };
      }
      msgs[0] = {
        ...msgs[0],
        textStringDTOList: [...msgs[0].textStringDTOList, { text: responseInput.trim() }],
      };
      return { ...prev, messagesDTO: msgs };
    });
    setResponseInput('');
  };

  const removeResponse = (idx: number) => {
    setEditForm((prev) => {
      const msgs = [...prev.messagesDTO];
      if (msgs[0]) {
        msgs[0] = { ...msgs[0], textStringDTOList: msgs[0].textStringDTOList.filter((_, i) => i !== idx) };
      }
      return { ...prev, messagesDTO: msgs };
    });
  };

  const addQuickReply = () => {
    if (!quickReplyInput.trim()) return;
    setEditForm((prev) => {
      const msgs = [...prev.messagesDTO];
      if (!msgs[0]) msgs[0] = { textStringDTOList: [] };
      const existing = msgs[0].quickRepliesDTO ?? { quickTextDTOList: [] };
      const nextText = quickReplyInput.trim();
      const alreadyExists = existing.quickTextDTOList.some(
        (item) => normalizeQuickText(item.text || '') === normalizeQuickText(nextText),
      );
      if (alreadyExists) {
        return prev;
      }
      msgs[0] = {
        ...msgs[0],
        quickRepliesDTO: {
          ...existing,
          quickTextDTOList: [...existing.quickTextDTOList, { text: nextText }],
        },
      };
      return { ...prev, messagesDTO: msgs };
    });
    setQuickReplyInput('');
  };

  const removeQuickReply = (idx: number) => {
    setEditForm((prev) => {
      const msgs = [...prev.messagesDTO];
      if (msgs[0]?.quickRepliesDTO) {
        msgs[0] = {
          ...msgs[0],
          quickRepliesDTO: {
            ...msgs[0].quickRepliesDTO!,
            quickTextDTOList: msgs[0].quickRepliesDTO!.quickTextDTOList.filter((_, i) => i !== idx),
          },
        };
      }
      return { ...prev, messagesDTO: msgs };
    });
  };

  // Genera quick replies con AI (Gemini) sulla base della tipologia dell'intent.
  // Il backend riceve displayName/action/training/risposta e deduce il contesto,
  // producendo 4 quick replies in italiano, in prima persona utente, con emoji iniziali.
  const onGenerateQuickRepliesAi = async () => {
    try {
      setQuickRepliesAiLoading(true);
      setError(null);

      const trainingTexts = (editForm.trainingPhraseDTO || [])
        .map((row) => String(row?.partsListDTO?.[0]?.text || '').trim())
        .filter(Boolean);
      const responseTexts = (editForm.messagesDTO?.[0]?.textStringDTOList || [])
        .map((row) => String(row?.text || '').trim())
        .filter(Boolean)
        .join(' | ');
      const experienceContext = (() => {
        if (selectedExperience === 'all') {
          const idExp = Number(editForm.idExperience || 0);
          return experiences.find((item) => item.id === idExp) || null;
        }
        return selectedExperienceData;
      })();

      const generated = await generateIntentQuickReplies({
        displayName: editForm.displayName || '',
        action: editForm.action || '',
        description: editForm.description || '',
        responseText: responseTexts,
        trainingPhrases: trainingTexts.slice(0, 8),
        experienceName: experienceContext?.name || '',
        experienceCity: experienceContext?.city || '',
        count: 4,
      });

      if (!Array.isArray(generated) || generated.length === 0) {
        setError('Nessuna quick reply generata. Riprova o aggiungi qualche training phrase.');
        return;
      }

      setEditForm((prev) => {
        const msgs = [...prev.messagesDTO];
        if (!msgs[0]) msgs[0] = { textStringDTOList: [] };
        const existing = msgs[0].quickRepliesDTO ?? { quickTextDTOList: [] };
        const existingNormalized = new Set(
          existing.quickTextDTOList.map((item) => normalizeQuickText(item.text || '')),
        );
        const newItems = generated
          .map((item) => String(item?.text || '').trim())
          .filter((text) => text.length > 0)
          .filter((text) => !isTokenLikeQuickReply(text))
          .filter((text) => !existingNormalized.has(normalizeQuickText(text)))
          .map((text) => ({ text }));
        msgs[0] = {
          ...msgs[0],
          quickRepliesDTO: {
            ...existing,
            quickTextDTOList: [...existing.quickTextDTOList, ...newItems],
          },
        };
        return { ...prev, messagesDTO: msgs };
      });
    } catch (err) {
      console.error('[onGenerateQuickRepliesAi] error:', err);
      setError('Errore nella generazione quick replies con AI. Riprova.');
    } finally {
      setQuickRepliesAiLoading(false);
    }
  };

  const onMediaUpload = async (file: File) => {
    const refId = editing?.id ?? `new-${Date.now()}`;
    setMediaUploading(true);
    try {
      // Determina il tipo legacy e il mime prefix dal file reale
      const mime = String(file.type || '').toLowerCase();
      const mediaType = mime.startsWith('image/')
        ? 4
        : mime.startsWith('audio/')
          ? 3
          : mime.startsWith('video/')
            ? 2
            : 0;
      if (!mediaType) {
        setError('Formato media non supportato. Usa immagine, audio o video.');
        setMediaUploading(false);
        return;
      }
      const url = await uploadFile(file, 'interactions', refId);
      setMediaUrl(url);
      setMediaMime(mime); // salva il mime per il preview (non dipende dall'estensione nell'URL)
      setEditForm((prev) => {
        const msgs = [...prev.messagesDTO];
        if (!msgs[0]) msgs[0] = { textStringDTOList: [] };
        msgs[0] = {
          ...msgs[0],
          mediaDTO: {
            links: [url],
            type: mediaType,
            name: String(file.name || '').trim() || undefined,
          },
        };
        return { ...prev, messagesDTO: msgs };
      });
    } catch (err) {
      console.error('[onMediaUpload] error:', err);
      setError('Upload media fallito. Verifica Glooci Platform.');
    } finally {
      setMediaUploading(false);
    }
  };

  const onRemoveMedia = () => {
    setMediaUrl('');
    setMediaMime('');
    setEditForm((prev) => {
      const msgs = [...prev.messagesDTO];
      if (!msgs[0]) msgs[0] = { textStringDTOList: [] };
      msgs[0] = {
        ...msgs[0],
        mediaDTO: null,
      };
      return { ...prev, messagesDTO: msgs };
    });
  };

  const onSave = async () => {
    if (!editForm.displayName.trim()) {
      setError('Il nome (displayName) è obbligatorio.');
      return;
    }
    const actionValue = String(editForm.action || '').trim();
    if (!editForm.isFallback && !actionValue) {
      setError('Per gli intent non fallback, il campo Azione è obbligatorio.');
      return;
    }
    const quickReplies = editForm.messagesDTO?.[0]?.quickRepliesDTO?.quickTextDTOList || [];
    const tokenLikeQuick = quickReplies.find((item) => isTokenLikeQuickReply(item.text || ''));
    if (tokenLikeQuick) {
      setError(
        `Quick reply non valida: "${tokenLikeQuick.text}". Usa testo naturale (es. "La mia canzone"), non token/eventi tipo "input.xxx".`,
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const scopeExperienceId = resolveInteractionExperienceId(
        editForm as Partial<LegacyInteraction>,
        typeof selectedExperience === 'number' ? selectedExperience : undefined,
      );
      const isStartIntentDraft = isStartLikeDraft(editForm);
      if (!scopeExperienceId || scopeExperienceId <= 0) {
        setError('Esperienza non valida per il salvataggio dell\'intent.');
        setSaving(false);
        return;
      }

      const sameExperienceStartIntents = interactions.filter((item) => {
        if (!item) return false;
        const itemExperienceId = resolveInteractionExperienceId(item, scopeExperienceId);
        return itemExperienceId === scopeExperienceId && isStartLikeInteraction(item);
      });
      const canonicalExistingStart = pickPrimaryStartIntent(
        sameExperienceStartIntents.filter((item) => Number(item.id) !== Number(editing?.id || 0)),
      );

      const normalizedDisplayName = String(editForm.displayName || '').trim().toLowerCase();
      const normalizedAction = String(editForm.action || '').trim().toLowerCase();
      const duplicate = interactions.find((item) => {
        if (!item) return false;
        if (!isNew && editing?.id && item.id === editing.id) return false;
        const itemExperienceId = resolveInteractionExperienceId(item, scopeExperienceId);
        if (scopeExperienceId && itemExperienceId !== scopeExperienceId) return false;
        const sameDisplayName =
          normalizedDisplayName
          && String(item.displayName || '').trim().toLowerCase() === normalizedDisplayName;
        const sameAction =
          normalizedAction
          && String(item.action || '').trim().toLowerCase() === normalizedAction;
        if (isStartIntentDraft && isStartLikeInteraction(item)) return false;
        return Boolean(sameDisplayName || sameAction);
      });
      if (duplicate) {
        setError(
          `Intent duplicato rilevato (nome o azione già presente: #${duplicate.id} "${duplicate.displayName}"). Apri quello esistente e modificalo.`,
        );
        setSaving(false);
        return;
      }

      const normalizedRecoveryPrompt = String(
        editForm.recoveryPrompt
          || editForm.promptRecupero
          || editForm.promptDiRecupero
          || editForm.geminiPrompt
          || '',
      ).trim();
      const fallbackResponseText =
        normalizedRecoveryPrompt
        || String(
          editForm.messagesDTO?.[0]?.textStringDTOList?.[0]?.text
          || editForm.description
          || editForm.displayName
          || 'Informazione disponibile.',
        ).trim();
      if (editForm.isFallback && !fallbackResponseText) {
        setError('Per un intent fallback serve almeno un testo risposta (o un prompt di recupero).');
        setSaving(false);
        return;
      }
      const ensuredAction = isStartIntentDraft
        ? START_INTENT_ACTION
        : (
          String(editForm.action || '').trim()
          || (editForm.isFallback ? 'gemini.fallback' : '')
        );
      const normalizedEvents = editForm.isFallback
        ? (
          Array.isArray(editForm.eventsDTO) && editForm.eventsDTO.length > 0
            ? editForm.eventsDTO
            : [{ name: 'input.unknown' }]
        )
        : (Array.isArray(editForm.eventsDTO) ? editForm.eventsDTO : []);
      const canonicalEvents = isStartIntentDraft
        ? buildCanonicalStartEvents(normalizedEvents)
        : normalizedEvents;
      const normalizedMessages = ensurePrimaryMessageText(editForm.messagesDTO, fallbackResponseText);
      const normalizedTrainingPhrases = DIRECT_INTENTS_ENABLED
        ? (Array.isArray(editForm.trainingPhraseDTO) ? editForm.trainingPhraseDTO : [])
        : [];
      const normalizedForm: Omit<LegacyInteraction, 'id'> = {
        ...editForm,
        idExperience: scopeExperienceId,
        // Sul path diretto Dialogflow manteniamo il valore corrente.
        // Il forcing a `true` serviva solo durante il fallback legacy.
        mlDisabled: DIRECT_INTENTS_ENABLED ? Boolean(editForm.mlDisabled) : true,
        isFallback: isStartIntentDraft ? false : editForm.isFallback,
        action: ensuredAction,
        trainingPhraseDTO: normalizedTrainingPhrases,
        eventsDTO: canonicalEvents,
        messagesDTO: normalizedMessages,
        recoveryPrompt: normalizedRecoveryPrompt,
        promptRecupero: normalizedRecoveryPrompt,
        promptDiRecupero: normalizedRecoveryPrompt,
        geminiPrompt: normalizedRecoveryPrompt,
      };

      let persisted: LegacyInteraction | null = null;
      let usedUpsertOnExistingStart = false;
      if (isNew) {
        if (isStartIntentDraft && canonicalExistingStart) {
          persisted = await updateInteraction({
            ...canonicalExistingStart,
            ...normalizedForm,
            id: canonicalExistingStart.id,
          } as LegacyInteraction);
          usedUpsertOnExistingStart = true;
        } else {
          persisted = await createInteraction(normalizedForm);
        }
      } else if (editing?.id) {
        persisted = await updateInteraction({
          ...editing,
          ...normalizedForm,
          id: editing.id,
        } as LegacyInteraction);
      }

      if (persisted) {
        setInteractions((prev) => {
          const withoutPersisted = prev.filter((item) => Number(item.id) !== Number(persisted?.id));
          return [persisted as LegacyInteraction, ...withoutPersisted];
        });
      }

      if (isStartIntentDraft && persisted) {
        const duplicateStartInteractions = interactions.filter((item) => {
          if (!item) return false;
          if (Number(item.id) === Number(persisted?.id)) return false;
          const itemExperienceId = resolveInteractionExperienceId(item, scopeExperienceId);
          return itemExperienceId === scopeExperienceId && isStartLikeInteraction(item);
        });

        const deletedIds: number[] = [];
        const failedIds: number[] = [];
        for (const duplicateInteraction of duplicateStartInteractions) {
          const duplicateId = Number(duplicateInteraction.id);
          try {
            // eslint-disable-next-line no-await-in-loop
            await deleteInteraction(duplicateInteraction);
            if (Number.isFinite(duplicateId) && duplicateId > 0) {
              deletedIds.push(duplicateId);
            }
          } catch (cleanupError) {
            console.warn('[INTENT_DEBUG] welcome intent duplicate cleanup failed', {
              duplicateId,
              duplicateDialogflowIntentId: duplicateInteraction.dialogflowIntentId,
              cleanupError,
            });
            if (Number.isFinite(duplicateId) && duplicateId > 0) {
              failedIds.push(duplicateId);
            }
          }
        }

        if (deletedIds.length > 0) {
          setInteractions((prev) => prev.filter((item) => !deletedIds.includes(Number(item.id))));
        }
        if (failedIds.length > 0) {
          setError(
            `Welcome intent salvato, ma ${failedIds.length} duplicat${failedIds.length === 1 ? 'o' : 'i'} non ${failedIds.length === 1 ? 'è stato eliminato' : 'sono stati eliminati'}. Riprova eliminazione manuale.`,
          );
        } else if (usedUpsertOnExistingStart) {
          setError(null);
        }
      }

      closeEditor();
    } catch (err) {
      console.error(err);
      setError('Errore nel salvataggio interazione.');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (interaction: LegacyInteraction) => {
    const interactionId = Number(interaction.id || 0);
    try {
      await deleteInteraction(interaction);
      setInteractions((prev) => prev.filter((item) => Number(item.id) !== interactionId));
      setDeleteConfirmInteraction(null);
    } catch (err) {
      const message = String((err as { message?: unknown } | null)?.message || '').toLowerCase();
      const isTimeout = message.includes('timed out') || message.includes('read timed out');
      console.error('[onDelete] error id=' + interactionId, err);
      if (isTimeout) {
        setError('Il server ha impiegato troppo tempo a rispondere (timeout). L\'intent potrebbe essere già stato eliminato: ricarica la pagina per verificare.');
      } else {
        setError('Errore eliminazione interazione. Riprova tra qualche secondo.');
      }
    }
  };

  const editTabs: { id: EditTab; label: string }[] = [
    { id: 'base', label: 'Base' },
    { id: 'training', label: 'Training Phrases' },
    { id: 'risposte', label: 'Risposte & Quick Replies' },
    { id: 'mappa', label: 'POI Mappa' },
    { id: 'media', label: 'Media' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Interazioni / Intent</h1>
          <p className="text-text-secondary mt-1">
            Gestione completa degli intent conversazionali: creazione, modifica, POI geografici e media su Glooci Platform.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-outline inline-flex items-center gap-2"
            onClick={() => {
              void loadInteractions(selectedExperience, searchText || undefined);
            }}
          >
            <RefreshCcw size={16} />
          </button>
          <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={openNew}>
            <Plus size={16} /> Nuovo Intent
          </button>
        </div>
      </div>

      <div className="card p-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <label className="text-sm lg:col-span-1">
          <span className="block mb-1 text-text-secondary">Esperienza</span>
          <select
            className="w-full rounded-lg border border-border px-3 py-2"
            value={selectedExperience === 'all' ? 'all' : String(selectedExperience)}
            onChange={async (e) => {
              const raw = e.target.value;
              const scope: ExperienceScope = raw === 'all' ? 'all' : Number(raw);
              setSelectedExperience(scope);
              if (scope === 'all') {
                setSearchParams({}, { replace: true });
              } else {
                setSearchParams({ experienceId: String(scope) }, { replace: true });
              }
              setLoading(true);
              try {
                await loadInteractions(scope, searchText || undefined);
              } catch (err) {
                console.error(err);
                setError('Errore caricamento interazioni.');
              } finally {
                setLoading(false);
              }
            }}
          >
            {isSuperAdmin && <option value="all">Tutte le esperienze</option>}
            {experiences.map((experience) => (
              <option key={experience.id} value={experience.id}>{experience.name}</option>
            ))}
          </select>
        </label>
        <label className="text-sm lg:col-span-2">
          <span className="block mb-1 text-text-secondary">Ricerca intent</span>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={18} />
            <input
              className="w-full rounded-lg border border-border pl-10 pr-3 py-2"
              placeholder="Nome intent, frase di addestramento o descrizione"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </label>
      </div>

      {selectedExperienceData && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <div className="card p-4 xl:col-span-2">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <GraduationCap size={15} className="text-primary" />
              Percorso laboratorio scuola
            </h2>
            <p className="text-xs text-text-secondary mt-1">
              Stai lavorando su: <span className="font-medium text-text-primary">{selectedExperienceData.name}</span>.
              Completa gli intent principali e poi rifinisci il prompt esperienza.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 text-xs">
              <Link
                to={`/esperienze?experienceId=${selectedExperienceData.id}`}
                className="rounded-lg border border-border px-3 py-2 hover:bg-gray-50 text-text-primary"
              >
                1. Esperienza
              </Link>
              <Link
                to={`/interazioni?experienceId=${selectedExperienceData.id}`}
                className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-primary"
              >
                2. Intent
              </Link>
              <Link
                to={`/prompt?experienceId=${selectedExperienceData.id}`}
                className="rounded-lg border border-border px-3 py-2 hover:bg-gray-50 text-text-primary"
              >
                3. Prompt IA
              </Link>
            </div>
          </div>
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-text-primary">Stato intent</h3>
            {selectedFlowStatus && (
              <p className="text-xs text-text-secondary mt-1">
                Completezza esperienza: {selectedFlowStatus.score}% ({selectedFlowStatus.completedRequired}/{selectedFlowStatus.totalRequired})
              </p>
            )}
            <p className={`text-xs mt-2 ${fallbackIntentCount > 0 ? 'text-green-700' : 'text-text-secondary'}`}>
              {fallbackIntentCount > 0
                ? `Fallback presenti: ${fallbackIntentCount}`
                : 'Fallback opzionale non configurato.'}
            </p>
          </div>
        </div>
      )}

      {error && <div className="rounded-lg border border-error/30 bg-error/10 p-4 text-error">{error}</div>}

      <p className="text-xs text-text-secondary">
        {loading ? 'Caricamento...' : `${filtered.length} / ${interactions.length} intent visualizzati`}
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {loading
          ? Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="card p-5 animate-pulse">
                <div className="h-5 bg-gray-200 rounded mb-3" />
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
                <div className="h-16 bg-gray-200 rounded" />
              </div>
            ))
          : filtered.map((interaction) => {
              const trainingCount = interaction.trainingPhraseDTO?.length || 0;
              const responseCount = interaction.messagesDTO?.[0]?.textStringDTOList?.length || 0;
              const quickCount = interaction.messagesDTO?.[0]?.quickRepliesDTO?.quickTextDTOList?.length || 0;
              const hasGeo = interaction.latitude != null && interaction.longitude != null;
              const hasMedia = Boolean(interaction.messagesDTO?.[0]?.mediaDTO?.links?.length);

              return (
                <article key={interaction.id} className="card p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-text-primary truncate">{interaction.displayName}</h3>
                      <p className="text-xs text-text-secondary mt-0.5">Azione: {interaction.action || 'n/d'}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {interaction.isFallback && (
                        <span className="text-xs rounded-full bg-error/15 text-error px-2 py-0.5">fallback</span>
                      )}
                      {hasGeo && <span title="Geolocalizzato"><MapPin size={14} className="text-primary" /></span>}
                      {hasMedia && <span title="Ha media"><Paperclip size={14} className="text-tertiary" /></span>}
                      <button
                        type="button"
                        onClick={() => openEditor(interaction)}
                        className="p-1.5 rounded hover:bg-gray-100 text-text-secondary hover:text-primary"
                        title="Modifica"
                      >
                        <Edit3 size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmInteraction(interaction)}
                        className="p-1.5 rounded hover:bg-error/10 text-text-secondary hover:text-error"
                        title="Elimina"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  <p className="text-xs text-text-secondary mt-2 line-clamp-2">
                    {interaction.description || 'Nessuna descrizione'}
                  </p>

                  <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
                    <span className="rounded bg-primary/10 text-primary px-2 py-0.5">
                      Training: {trainingCount}
                    </span>
                    <span className="rounded bg-accent/10 text-accent px-2 py-0.5">
                      Risposte: {responseCount}
                    </span>
                    {quickCount > 0 && (
                      <span className="rounded bg-tertiary/10 text-tertiary px-2 py-0.5">
                        Quick: {quickCount}
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
      </div>

      {(editing || isNew) && (
        <div className="fixed inset-0 z-50 bg-black/40 p-4 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-text-primary">
                {isNew ? 'Nuovo Intent' : `Modifica Intent #${editing?.id}`}
              </h2>
              <button type="button" onClick={closeEditor} className="p-2 rounded hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>

            <div className="flex border-b border-border overflow-x-auto px-6">
              {editTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setEditTab(t.id)}
                  className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                    editTab === t.id
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-text-secondary hover:text-primary'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-4">
              {editTab === 'base' && (
                <>
                  <label className="text-sm block">
                    <span className="block mb-1 text-text-secondary">Nome intent (displayName) *</span>
                    <input
                      className="w-full rounded-lg border border-border px-3 py-2 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                      value={editForm.displayName}
                      onChange={(e) => setEditForm((p) => ({ ...p, displayName: e.target.value }))}
                      placeholder="es. welcome.intent"
                      disabled={!isNew}
                      title={!isNew ? 'Il nome intent non e modificabile dopo la creazione.' : undefined}
                    />
                    {!isNew && (
                      <span className="mt-1 block text-xs text-text-secondary">
                        Il nome intent e bloccato dopo la creazione per evitare disallineamenti su Dialogflow.
                      </span>
                    )}
                  </label>
                  <label className="text-sm block">
                    <span className="block mb-1 text-text-secondary">Descrizione</span>
                    <textarea className="w-full rounded-lg border border-border px-3 py-2 min-h-[80px]" value={editForm.description || ''} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} />
                  </label>
                  <label className="text-sm block">
                    <span className="block mb-1 text-text-secondary">Azione</span>
                    <input
                      className="w-full rounded-lg border border-border px-3 py-2"
                      value={editForm.action || ''}
                      onChange={(e) => setEditForm((p) => ({ ...p, action: e.target.value }))}
                      placeholder="es. audio.canzone / luogo.rocca"
                    />
                  </label>
                  <label className="text-sm block">
                    <span className="block mb-1 text-text-secondary">Prompt di recupero IA (fallback)</span>
                    <textarea
                      className="w-full rounded-lg border border-border px-3 py-2 min-h-[120px]"
                      value={editForm.recoveryPrompt || ''}
                      onChange={(e) => setEditForm((p) => ({
                        ...p,
                        recoveryPrompt: e.target.value,
                        promptRecupero: e.target.value,
                        promptDiRecupero: e.target.value,
                        geminiPrompt: e.target.value,
                      }))}
                      placeholder="Istruzioni usate da Gemini quando scatta il recupero/fallback."
                    />
                  </label>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={editForm.isFallback ?? false} onChange={(e) => setEditForm((p) => ({ ...p, isFallback: e.target.checked }))} />
                      <span className="text-text-secondary">Intent di Fallback</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={editForm.mlDisabled ?? false} onChange={(e) => setEditForm((p) => ({ ...p, mlDisabled: e.target.checked }))} />
                      <span className="text-text-secondary">ML Disabilitato</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={editForm.resetContexts ?? false} onChange={(e) => setEditForm((p) => ({ ...p, resetContexts: e.target.checked }))} />
                      <span className="text-text-secondary">Reset Contesti</span>
                    </label>
                  </div>
                </>
              )}

              {editTab === 'training' && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      className="flex-1 rounded-lg border border-border px-3 py-2 text-sm"
                      value={trainingInput}
                      onChange={(e) => setTrainingInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTraining(); } }}
                      placeholder="Aggiungi training phrase e premi Invio"
                    />
                    <button type="button" onClick={addTraining} className="btn-outline px-3">
                      <Plus size={16} />
                    </button>
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {editForm.trainingPhraseDTO.length === 0 ? (
                      <p className="text-xs text-text-secondary italic">Nessuna training phrase</p>
                    ) : (
                      editForm.trainingPhraseDTO.map((tp, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                          <span className="text-sm flex-1">{tp.partsListDTO[0]?.text}</span>
                          <button type="button" onClick={() => removeTraining(idx)} className="text-error hover:text-error/80">
                            <X size={14} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {editTab === 'risposte' && (
                <div className="space-y-5">
                  <div>
                    <p className="text-sm font-medium text-text-primary mb-2">Risposte testuali</p>
                    <div className="flex gap-2 mb-2">
                      <input className="flex-1 rounded-lg border border-border px-3 py-2 text-sm" value={responseInput} onChange={(e) => setResponseInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addResponse(); } }} placeholder="Aggiungi una risposta..." />
                      <button type="button" onClick={addResponse} className="btn-outline px-3"><Plus size={16} /></button>
                    </div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {(editForm.messagesDTO?.[0]?.textStringDTOList || []).map((msg, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded px-3 py-1.5">
                          <span className="text-sm flex-1">{msg.text}</span>
                          <button type="button" onClick={() => removeResponse(idx)} className="text-error"><X size={13} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-text-primary">Quick Replies</p>
                      <button
                        type="button"
                        onClick={() => void onGenerateQuickRepliesAi()}
                        disabled={quickRepliesAiLoading}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 disabled:opacity-60 disabled:cursor-not-allowed text-primary text-xs font-medium px-2.5 py-1.5"
                        title="Gemini deduce la tipologia dell'intent (POI, storia, gastronomia, evento, start...) e genera 4 quick replies coerenti con emoji iniziali."
                      >
                        <Sparkles size={13} className={quickRepliesAiLoading ? 'animate-pulse' : ''} />
                        {quickRepliesAiLoading ? 'Generazione AI...' : 'Genera con AI'}
                      </button>
                    </div>
                    <p className="text-xs text-text-secondary mb-2">
                      Inserisci testi naturali che corrispondono a training phrase esistenti. Con "Genera con AI" Glooci Platform riconosce la tipologia dell'intent e crea 4 quick replies contestuali con emoji.
                    </p>
                    <div className="flex gap-2 mb-2">
                      <input className="flex-1 rounded-lg border border-border px-3 py-2 text-sm" value={quickReplyInput} onChange={(e) => setQuickReplyInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addQuickReply(); } }} placeholder="Aggiungi quick reply..." />
                      <button type="button" onClick={addQuickReply} className="btn-outline px-3"><Plus size={16} /></button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(editForm.messagesDTO?.[0]?.quickRepliesDTO?.quickTextDTOList || []).map((qr, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
                          {qr.text}
                          <button type="button" onClick={() => removeQuickReply(idx)} className="hover:text-error"><X size={10} /></button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {editTab === 'mappa' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-text-primary mb-1 flex items-center gap-1">
                      <MapPin size={15} /> POI geografico dell'intent
                    </p>
                    <p className="text-xs text-text-secondary mb-3">
                      Associa le coordinate geografiche a questo intent per la visualizzazione su mappa nell'app mobile.
                    </p>
                  </div>
                  <MapCoordinatePicker
                    latitude={editForm.latitude ?? null}
                    longitude={editForm.longitude ?? null}
                    onChange={(lat, lng) => setEditForm((p) => ({ ...p, latitude: lat, longitude: lng }))}
                    label="Coordinate POI"
                  />
                </div>
              )}

              {editTab === 'media' && (
                <div className="space-y-4">
                  <p className="text-sm font-medium text-text-primary mb-1 flex items-center gap-1">
                    <Paperclip size={15} /> Media associato all'intent
                  </p>
                  {mediaUrl ? (
                    <div className="rounded-lg border border-border p-4">
                      {(() => {
                        // Priorità: mime salvato dallo state → estensione nel path URL → fallback audio
                        const urlPath = mediaUrl.split('?')[0].toLowerCase();
                        const isImage = mediaMime.startsWith('image/') || urlPath.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                        const isVideo = mediaMime.startsWith('video/') || urlPath.match(/\.(mp4|mov|m4v|webm)$/i);
                        const isAudio = mediaMime.startsWith('audio/') || urlPath.match(/\.(mp3|wav|ogg|aac|m4a|flac)$/i);
                        if (isImage) return <img src={mediaUrl} alt="Media" className="max-h-52 rounded-lg object-contain" />;
                        if (isVideo) return <video controls src={mediaUrl} className="w-full rounded-lg" />;
                        if (isAudio || true) return <audio controls src={mediaUrl} className="w-full" />;
                      })()}
                      <p className="text-xs text-text-secondary mt-2 truncate">{mediaUrl}</p>
                      <button type="button" onClick={onRemoveMedia} className="mt-2 text-error text-xs flex items-center gap-1"><X size={12} /> Rimuovi</button>
                    </div>
                  ) : (
                    <div className="rounded-lg border-2 border-dashed border-border p-8 text-center">
                      <Paperclip size={32} className="mx-auto mb-3 text-text-secondary/40" />
                      <p className="text-sm text-text-secondary">Nessun media associato</p>
                    </div>
                  )}
                  <label className="btn-outline text-sm cursor-pointer px-4 py-2 inline-block">
                    {mediaUploading ? 'Upload in corso...' : 'Carica immagine / audio / video su Glooci Platform'}
                    <input
                      type="file"
                      accept="image/*,audio/*,video/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void onMediaUpload(file);
                      }}
                    />
                  </label>
                </div>
              )}
            </div>

            {error && (
              <div className="mx-6 mb-4 rounded-lg border border-error/30 bg-error/10 p-3 text-error text-sm">{error}</div>
            )}

            <div className="sticky bottom-0 bg-white border-t border-border px-6 py-4 flex justify-end gap-3">
              <button type="button" className="btn-outline" onClick={closeEditor}>Annulla</button>
              <button
                type="button"
                className="btn-primary inline-flex items-center gap-2"
                onClick={onSave}
                disabled={saving}
              >
                <Save size={16} /> {saving ? 'Salvataggio...' : isNew ? 'Crea Intent' : 'Salva Modifiche'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmInteraction !== null && (
        <div className="fixed inset-0 z-50 bg-black/40 p-4 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-text-primary mb-2">Conferma eliminazione</h3>
            <p className="text-text-secondary text-sm mb-6">
              Stai per eliminare l'intent <strong>{deleteConfirmInteraction.displayName || `#${deleteConfirmInteraction.id}`}</strong>. L'operazione è irreversibile.
            </p>
            <div className="flex justify-end gap-3">
              <button type="button" className="btn-outline" onClick={() => setDeleteConfirmInteraction(null)}>Annulla</button>
              <button
                type="button"
                className="bg-error hover:bg-error/90 text-white font-medium py-2 px-4 rounded-lg"
                onClick={() => void onDelete(deleteConfirmInteraction)}
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractionsLegacy;
